import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import './Dashboard.css';
import { Sparkles, Star, Compass, Eye, MessageCircle, TrendingUp, Shield, UserRound } from 'lucide-react';
import MapaAstral from '../components/MapaAstral';
import SocialFeed from '../components/SocialFeed';
import DashboardHome from '../components/DashboardHome';
import DashboardLite from '../components/DashboardLite';
import Insights from "../components/Insights";
import InsightsLite from '../components/InsightsLite';
import ChatAI from '../components/ChatAI';
import InvestmentAnalysis from '../components/InvestmentAnalysis';
import ProFeature from '../components/ProFeature';
import ProBadge from '../components/ProBadge';
import SubscriptionExpirationWarningButton from '../components/SubscriptionExpirationWarningButton';
import { useSubscription } from '../SubscriptionProvider';
import { PLATFORM_FEATURES } from '../platform/features';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@radix-ui/react-tabs';
import { IonContent, IonPage } from '@ionic/react';
import { Button } from '../components/ui/button';
import useAstroApiClient from '../hooks/useAstroApiClient';

const Dashboard: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();

  const auth = useAuth();
  const profile = auth.user?.profile as Record<string, unknown> | undefined;
  const rolesClaim = (profile?.role as unknown) ?? (profile as Record<string, unknown> | undefined)?.roles;
  const roles = Array.isArray(rolesClaim) ? rolesClaim : typeof rolesClaim === 'string' ? [rolesClaim] : [];
  const isAdmin = roles.includes('Admin');
  const [userName, setUserName] = useState('');
  const api = useAstroApiClient();
  const { hasFeature } = useSubscription();
  const hasProDailyDashboard = hasFeature(PLATFORM_FEATURES.DailyReport);
  const hasProTransitReading = hasFeature(PLATFORM_FEATURES.TransitReading);

  const [activeTab, setActiveTab] = useState('dashboard');
  const [showLeftGradient, setShowLeftGradient] = useState(false);
  const [showRightGradient, setShowRightGradient] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const safeAreaTop = 'var(--ion-safe-area-top, env(safe-area-inset-top))';
  const safeAreaBottom = 'var(--ion-safe-area-bottom, env(safe-area-inset-bottom))';

  useEffect(() => {
    const handleScroll = () => {
      if (scrollContainerRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current
        setShowLeftGradient(scrollLeft > 10)
        setShowRightGradient(scrollLeft < scrollWidth - clientWidth - 10)
      }
    }

    const container = scrollContainerRef.current
    if (container) {
      handleScroll()
      container.addEventListener('scroll', handleScroll)
      return () => container.removeEventListener('scroll', handleScroll)
    }
  }, [])

  useEffect(() => {
    const handleResize = () => {
      if (scrollContainerRef.current) {
        const { scrollWidth, clientWidth } = scrollContainerRef.current
        setShowRightGradient(scrollWidth > clientWidth)
      }
    }

    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    const loadUserName = async () => {
      if (!auth.user) return;
      try {
        const user = await api.apiUsersAuth();
        if (user?.name) {
          setUserName(user.name);
        }
      } catch (error) {
        console.error('Failed to load user name', error);
      }
    };

    loadUserName();
  }, [auth.user, api]);

  return (
    <IonPage id="dashboard-page">
      <IonContent
        fullscreen
        className="bg-gradient-to-br from-slate-50 via-stone-100 to-gray-100"
      >
        <div
          className="min-h-screen"
          style={{
            minHeight: '100dvh',
            paddingBottom: `max(0.5rem, ${safeAreaBottom})`,
          }}
        >
          <div
            className="sticky top-0 z-40 bg-white/85 shadow-sm backdrop-blur-sm"
            style={{
              paddingTop: `max(0.5rem, ${safeAreaTop})`,
            }}
          >
            <header className="border-b border-stone-200">
              <div className="container mx-auto px-4 py-2.5 md:py-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center space-x-2 min-w-0">
                    <div className="relative shrink-0">
                      <Star className="h-6 w-6 md:h-8 md:w-8 text-amber-500" />
                      <Sparkles className="absolute -top-1 -right-1 h-3 w-3 md:h-4 md:w-4 text-action-500" />
                    </div>
                    <div className="min-w-0">
                      <h1 className="truncate text-lg font-bold text-slate-800 md:text-2xl">Senti</h1>
                      <p className="hidden text-xs text-slate-600 sm:block md:text-sm">{t('personalizedCosmicJourney')}</p>
                    </div>
                  </div>

                  <div className="hidden flex-1 justify-center lg:flex">
                    <p className="whitespace-nowrap text-sm font-medium text-slate-700 md:text-base">
                      {t('hello')},{" "}
                      <span className="text-action-700">{userName}</span>
                    </p>
                  </div>

                  <div className="hidden items-center space-x-4 md:flex">
                    <SubscriptionExpirationWarningButton />
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-stone-300 bg-transparent text-slate-700 hover:bg-stone-50"
                      onClick={() => history.push('/account')}
                    >
                      <UserRound className="mr-2 h-4 w-4" />
                      {t('myData')}
                    </Button>
                    {isAdmin && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-action-300 bg-transparent text-action-700 hover:bg-action-50"
                        onClick={() => history.push('/admin')}
                      >
                        <Shield className="mr-2 h-4 w-4" />
                        Admin
                      </Button>
                    )}
                    {auth.isAuthenticated ? (
                      <Button onClick={() => auth.signoutRedirect()} className="bg-action-700 text-white hover:bg-action-800">
                        {t('logout')}
                      </Button>
                    ) : (
                      <Button onClick={() => auth.signinRedirect()} className="bg-action-700 text-white hover:bg-action-800">
                        {t('login')}
                      </Button>
                    )}
                  </div>

                  <div className="flex items-center space-x-2 md:hidden">
                    <SubscriptionExpirationWarningButton compact />
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 border-stone-300 bg-transparent px-2 text-slate-700 hover:bg-stone-50"
                      onClick={() => history.push('/account')}
                      aria-label={t('myData')}
                      title={t('myData')}
                    >
                      <UserRound className="h-3 w-3" />
                    </Button>
                    {isAdmin && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 border-action-300 bg-transparent px-2 text-action-700 hover:bg-action-50"
                        onClick={() => history.push('/admin')}
                      >
                        <Shield className="h-3 w-3" />
                      </Button>
                    )}
                    {auth.isAuthenticated ? (
                      <Button
                        onClick={() => auth.signoutRedirect()}
                        size="sm"
                        className="h-7 bg-action-700 px-3 py-1 text-xs text-white hover:bg-action-800"
                      >
                        {t('logout')}
                      </Button>
                    ) : (
                      <Button
                        onClick={() => auth.signinRedirect()}
                        size="sm"
                        className="h-7 bg-action-700 px-3 py-1 text-xs text-white hover:bg-action-800"
                      >
                        {t('login')}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </header>

            <nav className="relative border-b border-stone-200 bg-white/70">
              <div className="container relative mx-auto px-2 md:px-4">
                {showLeftGradient && (
                  <div className="pointer-events-none absolute bottom-0 left-0 top-0 z-10 w-12 bg-gradient-to-r from-white/90 to-transparent md:hidden" />
                )}

                {showRightGradient && (
                  <div className="pointer-events-none absolute bottom-0 right-0 top-0 z-10 w-12 bg-gradient-to-l from-white/90 to-transparent md:hidden" />
                )}
                <div ref={scrollContainerRef} className="overflow-x-auto scrollbar-hide">
                  <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                    <TabsList className="grid h-auto min-w-max w-full grid-flow-col auto-cols-fr bg-transparent p-0 md:min-w-0 md:grid-cols-5">
                      <TabsTrigger
                        value="dashboard"
                        className="flex flex-col items-center space-y-1 px-3 py-2 md:px-4 md:py-3 data-[state=active]:bg-action-100 data-[state=active]:text-action-800"
                      >
                        <Compass className="h-4 w-4 md:h-5 md:w-5" />
                        <span className="text-xs">
                          {t('dashboard')}
                          <ProBadge feature={PLATFORM_FEATURES.Dashboard} />
                        </span>
                      </TabsTrigger>
                      <TabsTrigger
                        value="chart"
                        className="flex flex-col items-center space-y-1 px-3 py-2 md:px-4 md:py-3 data-[state=active]:bg-action-100 data-[state=active]:text-action-800"
                      >
                        <Star className="h-4 w-4 md:h-5 md:w-5" />
                        <span className="text-xs">
                          {t('chart')}
                          <ProBadge feature={PLATFORM_FEATURES.NatalChart} />
                        </span>
                      </TabsTrigger>
                      <TabsTrigger
                        value="insights"
                        className="flex flex-col items-center space-y-1 px-3 py-2 md:px-4 md:py-3 data-[state=active]:bg-action-100 data-[state=active]:text-action-800"
                      >
                        <Eye className="h-4 w-4 md:h-5 md:w-5" />
                        <span className="text-xs">
                          {t('insights')}
                          <ProBadge feature={PLATFORM_FEATURES.Insights} />
                        </span>
                      </TabsTrigger>
                      <TabsTrigger
                        value="chat"
                        className="flex flex-col items-center space-y-1 px-3 py-2 md:px-4 md:py-3 data-[state=active]:bg-action-100 data-[state=active]:text-action-800"
                      >
                        <MessageCircle className="h-4 w-4 md:h-5 md:w-5" />
                        <span className="text-xs">
                          {t('chat')}
                          <ProBadge feature={PLATFORM_FEATURES.AiChat} />
                        </span>
                      </TabsTrigger>
                      <TabsTrigger
                        value="investments"
                        className="flex flex-col items-center space-y-1 px-3 py-2 md:px-4 md:py-3 data-[state=active]:bg-action-100 data-[state=active]:text-action-800"
                      >
                        <TrendingUp className="h-4 w-4 md:h-5 md:w-5" />
                        <span className="text-xs">
                          {t('investments')}
                        </span>
                      </TabsTrigger>
                    </TabsList>
                  </Tabs>
                </div>
              </div>
            </nav>
          </div>

          <main className="container mx-auto px-3 py-4 pb-20 md:px-4 md:py-6 md:pb-6">
            <Tabs value={activeTab} className="w-full">
              <TabsContent value="dashboard" className="space-y-4 md:space-y-6">
                <ProFeature feature={PLATFORM_FEATURES.Dashboard}>
                  {hasProDailyDashboard ? <DashboardHome /> : <DashboardLite />}
                </ProFeature>
              </TabsContent>
              <TabsContent value="chart" className="space-y-4 md:space-y-6">
                <ProFeature feature={PLATFORM_FEATURES.NatalChart}>
                  <MapaAstral />
                </ProFeature>
              </TabsContent>
              <TabsContent value="insights" className="space-y-4 md:space-y-6">
                <ProFeature feature={PLATFORM_FEATURES.Insights}>
                  {hasProTransitReading ? <Insights /> : <InsightsLite />}
                </ProFeature>
              </TabsContent>
              <TabsContent value="chat" className="space-y-4 md:space-y-6">
                <ProFeature feature={PLATFORM_FEATURES.AiChat}>
                  <ChatAI />
                </ProFeature>
              </TabsContent>
              <TabsContent value="social" className="space-y-4 md:space-y-6">
                <ProFeature feature={PLATFORM_FEATURES.Social}>
                  <SocialFeed />
                </ProFeature>
              </TabsContent>
              <TabsContent value="investments" className="space-y-4 md:space-y-6">
                <ProFeature feature={PLATFORM_FEATURES.Investments}>
                  <InvestmentAnalysis />
                </ProFeature>
              </TabsContent>
            </Tabs>
          </main>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default Dashboard;
